/**
 * contentValidators/ruleEnumerationChecks.ts
 *
 * SWW/SPR leenwoorden-audit (14 juni 2026).
 *
 * Detects "rule-enumeration meta-prose" leaking into the question stem —
 * the dictatish "regel i.p.v. het zo gek in de tekst zetten" finding. When an
 * author crams the solution rationale into the stem ("welke drie regels moet
 * je tegelijk afwegen", "combineert correct: X, Y én Z", "welke vorm is
 * correct en welke denkfout maakt de leerling"), two things break:
 *
 *   1. The stem becomes a wall of rule-listing — unreadable for 9-12 yr olds
 *      (Sweller extraneous cognitive load / APP_FEEL §P6 "Eenvoud Wint").
 *   2. It often asks a SECOND question the answer options cannot answer
 *      (the options are word-forms, the second clause asks for rules) — a
 *      stem/answer mismatch.
 *
 * The rule belongs in a `rule`/support-aid, not in the stem.
 *
 * SHADOW-MODE ONLY: always emitted as `info` — never blocks, never escalates
 * via the enforce-flag. Pure telemetry so we can MEASURE the spread across all
 * modules before deciding on a harder gate, per the "observability before
 * enforcement" discipline (ARCHITECTURAL_PEER_REVIEW_PLAN_2026 §FG4 / §F0.B).
 */

import type { ValidationIssue } from '../../issueContract'
import { info } from '../../issueContract'
import type { Question } from '../../../../content'
import { STAGE } from './shared'
import { SEVERITY } from '../../severityScale'

type RuleEnumReason =
  | 'rule_count_enumeration'
  | 'combine_correct_list'
  | 'combine_rule_list'
  | 'double_question'

interface RuleEnumPattern {
  reason: RuleEnumReason
  regex: RegExp
  label: string
  suggestion: string
}

/**
 * Conservative, Dutch-specific patterns. Tuned to fire on the leenwoorden
 * stems flagged in the audit (race/upload/masseren/parkeren/timed) while
 * avoiding false positives on legitimate single-task stems.
 */
const PATTERNS: RuleEnumPattern[] = [
  {
    reason: 'rule_count_enumeration',
    // "welke drie regels moet je …", "welke 3 regels …",
    // "welke regels moet je tegelijk afwegen/combineren"
    regex:
      /welke\s+(twee|drie|vier|\d+)?\s*regels?\s+(moet|kun|ga)\s+je\b|regels?\s+moet\s+je\s+(tegelijk\s+)?(afweg|combiner|toepass)/i,
    label: 'regel-opsomming in de stam',
    suggestion:
      'Stel één heldere taak in de stam (bijv. "Wat is de verleden tijd van X in: …?") en verplaats de regels naar het rule/support-aid.',
  },
  {
    reason: 'combine_correct_list',
    // "combineert correct: …, … én …" / "combineer correct: … en …"
    regex: /combineer(t|en)?\s+correct\s*:/i,
    label: 'regel-checklist ("combineert correct: … én …") in de stam',
    suggestion:
      'Haal de "combineert correct: X, Y én Z"-opsomming uit de stam; de afzonderlijke regels horen in het rule-aid (showWhen: "wrong").',
  },
  {
    reason: 'combine_rule_list',
    // Imperative rule-listing: "Combineer Franse stam-spelling, kofschip-regel
    // (r) en ge-prefix." A "Combineer …" stem that enumerates rule-nouns
    // (stam/regel/spelling/uitgang/prefix/kofschip) across a comma + " en "
    // three-item list. Conservative: the rule-noun anchor avoids firing on
    // legitimate stems like "Combineer de twee breuken".
    regex:
      /\bcombineer\b[^.?!]*\b(stam|regel|spelling|uitgang|prefix|kofschip)\b[^.?!]*,[^.?!]*\ben\b/i,
    label: 'regel-opsomming ("Combineer X, Y en Z") in de stam',
    suggestion:
      'Vraag in de stam alleen naar de vorm (bijv. "Typ het voltooid deelwoord van X."); de te combineren regels horen in het rule/support-aid (showWhen: "wrong").',
  },
  {
    reason: 'double_question',
    // "… en welke denkfout maakt …" / "… en welke regels …"
    // A stem that asks the form AND the rationale/denkfout in one breath.
    regex: /\ben\s+welke\s+(denkfout|regels?|fout)\b/i,
    label: 'dubbele vraag (vorm + denkfout/regel) in de stam',
    suggestion:
      'Vraag in de stam alleen naar de vorm; de denkfout wordt al gevangen door pedagogy.misconceptionTags op de distractors en in de feedback.',
  },
]

/**
 * Validate that the question stem does not enumerate solution rules /
 * rationale. Emits at most one `info` finding per matched pattern.
 */
export function validateRuleEnumerationInStem(
  question: Question,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const stem = question.question ?? ''
  if (!stem) return issues

  const qId = question.id ?? 'UNKNOWN'
  const fId = question.familyId ?? 'UNKNOWN'

  for (const pattern of PATTERNS) {
    const match = stem.match(pattern.regex)
    if (match) {
      const matched = match[0].trim()
      issues.push(
        info(
          'content',
          STAGE,
          'CONTENT_RULE_ENUMERATION_IN_STEM',
          `Stam bevat ${pattern.label} ("${matched}") — verplaats de regel naar een rule/support-aid (leesbaarheid + voorkomt stam/antwoord-mismatch)`,
          {
            field: 'question',
            questionId: qId,
            familyId: fId,
            severity: SEVERITY.NEAR_MINOR,
            suggestion: pattern.suggestion,
            metadata: { reason: pattern.reason, match: matched },
          },
        ),
      )
    }
  }

  return issues
}
